import { orgsApi, getApiErrorMessage } from '@milliswift/api-client'
import { useAuth } from '@milliswift/auth'
import { Button, Callout, useToast } from '@milliswift/ui'
import { CheckCircle2, Users } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AuthCard, AuthPage } from '../components/AuthCard'

/**
 * Invitation acceptance.
 *
 * The invite token is accepted by whichever user is signed in. There is no
 * pre-accept "view invite details" endpoint, so this page cannot preview the
 * organisation name — the user sees a clear explanation of what accepting
 * does instead, which is the honest version of what the original spec wanted.
 */
export default function Invite() {
  const { token = '' } = useParams()
  const { isAuthenticated, status } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()

  const [busy, setBusy] = useState(false)
  const [accepted, setAccepted] = useState(false)

  const accept = async () => {
    if (!token || busy) return
    setBusy(true)
    try {
      await orgsApi.acceptInvite(token)
      setAccepted(true)
    } catch (error) {
      toast.error('Could not accept the invitation', getApiErrorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  /* ---------------- signed out: sign in first, the token waits in the URL ---------------- */

  if (status === 'loading') {
    return (
      <AuthPage>
        <AuthCard title="Checking your session">
          <p className="py-6 text-center text-sm text-muted">One moment…</p>
        </AuthCard>
      </AuthPage>
    )
  }

  if (!isAuthenticated) {
    return (
      <AuthPage>
        <AuthCard
          title="You've been invited"
          subtitle="Sign in first — the invitation will be waiting."
        >
          <div className="space-y-4">
            <Callout tone="info" icon={<Users className="h-4 w-4" />}>
              Invitations attach to the account you sign in with, so make sure it's the one
              you want to join the organisation.
            </Callout>

            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" onClick={() => navigate(`/login?return_to=${encodeURIComponent(window.location.href)}`)}>
                Sign in
              </Button>
              <Button onClick={() => navigate(`/signup?return_to=${encodeURIComponent(window.location.href)}`)}>
                Create account
              </Button>
            </div>
          </div>
        </AuthCard>
      </AuthPage>
    )
  }

  /* ---------------- signed in: accept or decline ---------------- */

  if (accepted) {
    return (
      <AuthPage>
        <AuthCard title="You're in">
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-soft">
              <CheckCircle2 className="h-6 w-6 text-success-text" aria-hidden />
            </span>
            <p className="text-sm text-muted">
              Invitation accepted. Head to the console to get started.
            </p>
            <Button className="w-full" onClick={() => (window.location.href = '/dashboard')}>
              Go to the console
            </Button>
          </div>
        </AuthCard>
      </AuthPage>
    )
  }

  return (
    <AuthPage>
      <AuthCard title="Join this organisation" subtitle="Someone invited this account to collaborate.">
        <div className="space-y-5">
          <Callout tone="info">
            Accepting gives the organisation's members visibility of your account within
            milliswift, subject to the role the admin assigned.
          </Callout>

          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => (window.location.href = '/login')}>
              Not now
            </Button>
            <Button loading={busy} disabled={!token} onClick={() => void accept()}>
              Accept
            </Button>
          </div>

          {!token ? (
            <p className="text-center text-caption text-danger-text">
              This link is incomplete — ask for a fresh invitation.
            </p>
          ) : null}
        </div>
      </AuthCard>
    </AuthPage>
  )
}
